/**
 * CustomerReferral — `/refer` — show the customer's referral code, list of
 * shops they've referred, and per-shop earnings. Share-link copy + native
 * share-sheet on mobile.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Check, Share2, Store, Wallet, IndianRupee, Plus, X, Clock, CheckCircle2, XCircle, Inbox, Users, Coins } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { BkPageHeader } from "../../components/blinkit/BkUI";

const EMPTY_FRIEND_REFERRAL = {
    enabled: false, bonus_coins: 0, bonus_wallet_amount: 0,
    friends_referred: 0, coins_earned: 0, wallet_earned: 0,
};

export default function CustomerReferral() {
    // Blinkit-theme tenants get the shared Blinkit header; the page body is the same for every theme.
    const blinkit = useIsBlinkit();
    const [data, setData] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [copied, setCopied] = useState(false);
    const [copiedFriend, setCopiedFriend] = useState(false);
    const [showAddShop, setShowAddShop] = useState(false);

    const loadSubmissions = useCallback(async () => {
        try {
            const { data } = await api.get("/customer/shop-submissions");
            setSubmissions(Array.isArray(data) ? data : []);
        } catch { /* fine — endpoint may not exist on legacy tenants */ }
    }, []);

    useEffect(() => {
        api.get("/customer/referral").then(({ data }) => setData(data)).catch(() => setData({ code: "", shops: [], transactions: [], summary: { total_earned: 0, shops_linked: 0, orders_credited: 0 }, config: {}, friend_referral: EMPTY_FRIEND_REFERRAL }));
        loadSubmissions();
    }, [loadSubmissions]);

    if (!data) return <div className="p-6 flex items-center justify-center text-muted-foreground">Loading…</div>;

    const cfg = data.config || {};
    const shareText = cfg.mode === "ONE_TIME"
        ? `Use code ${data.code} when listing your shop on ${window.location.host.replace(/^www\./, "")} — I'll earn ${inr(cfg.one_time_amount)} once your shop fulfils its first order.`
        : `Use code ${data.code} when listing your shop on ${window.location.host.replace(/^www\./, "")} — I'll earn ${cfg.recurring_pct}% on every order.`;
    const shareUrl = `${window.location.origin}/?ref=${data.code}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
            toast.success("Copied to clipboard");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not copy"); }
    };

    const share = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "Refer a shop", text: shareText, url: shareUrl }); }
            catch { /* user dismissed */ }
        } else {
            copy();
        }
    };

    // "App Referral" — invite a friend to the app (as opposed to the shop
    // referral above). Same code/link, different reward: the friend's
    // FIRST delivered order credits you with coins (backend already
    // handled this — coins_router.award_customer_referral_for_order —
    // it just had no UI to discover or share it from).
    const friendReferral = data.friend_referral || EMPTY_FRIEND_REFERRAL;
    // Tenants can configure a coins bonus, a wallet (₹) bonus, or both —
    // build whichever human sentence matches what's actually configured.
    const friendRewardParts = [
        friendReferral.bonus_wallet_amount > 0 ? inr(friendReferral.bonus_wallet_amount) : null,
        friendReferral.bonus_coins > 0 ? `${friendReferral.bonus_coins} coins` : null,
    ].filter(Boolean);
    const friendRewardText = friendRewardParts.join(" + ") || `${friendReferral.bonus_coins} coins`;
    const friendShareText = `Join me on ${window.location.host.replace(/^www\./, "")} — use my code ${data.code} and I'll earn ${friendRewardText} once you place your first order.`;

    const copyFriend = async () => {
        try {
            await navigator.clipboard.writeText(`${friendShareText} ${shareUrl}`);
            setCopiedFriend(true);
            setTimeout(() => setCopiedFriend(false), 1500);
            toast.success("Copied to clipboard");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not copy"); }
    };

    const shareFriend = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: "Invite a friend", text: friendShareText, url: shareUrl }); }
            catch { /* user dismissed */ }
        } else {
            copyFriend();
        }
    };

    return (
        <div className="pb-32" data-testid="customer-referral">
            {blinkit ? (
                <BkPageHeader title="Refer a shop · Earn" back="/profile" />
            ) : (
                <header className="sticky top-0 bg-background z-10 px-4 py-3 border-b border-border flex items-center gap-3">
                    <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-muted"><ArrowLeft size={20} /></Link>
                    <h1 className="text-lg font-bold">Refer a Shop · Earn</h1>
                </header>
            )}

            {/* iter178 — desktop (lg+): two columns, same convention as Cart / Checkout. Below lg both wrappers are plain
                blocks, so phones and tablets render exactly as before. */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-start">
            <div className="min-w-0">
            {/* Hero — code + share */}
            <div className="mx-4 mt-4 rounded-2xl p-5 text-white shadow-md" style={{ background: "linear-gradient(135deg,#7C5CFF 0%,#3B82F6 50%,#06B6D4 100%)" }} data-testid="referral-hero">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-90">Your referral code</p>
                <div className="mt-2 flex items-baseline gap-3">
                    <code className="text-3xl font-extrabold tracking-wider font-mono" data-testid="referral-code">{data.code || "—"}</code>
                </div>
                <p className="text-xs mt-3 opacity-90">
                    {cfg.mode === "ONE_TIME"
                        ? `Earn ${inr(cfg.one_time_amount || 0)} for every shop that signs up with your code and fulfils its first order.`
                        : `Earn ${cfg.recurring_pct || 0}% on every order from every shop signed up using your code — forever.`}
                </p>
                <div className="mt-4 flex gap-2">
                    <button data-testid="referral-copy" onClick={copy} className="flex-1 bg-white/15 hover:bg-white/25 rounded-xl py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2">
                        {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy invite</>}
                    </button>
                    <button data-testid="referral-share" onClick={share} className="bg-white text-foreground rounded-xl px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2">
                        <Share2 size={14} /> Share
                    </button>
                </div>
            </div>

            {/* KPI tiles */}
            <div className="mx-4 mt-4 grid grid-cols-3 gap-2">
                <Tile icon={IndianRupee} label="Total earned" value={inr(data.summary?.total_earned || 0)} />
                <Tile icon={Store} label="Shops linked" value={data.summary?.shops_linked || 0} />
                <Tile
                    icon={Inbox}
                    label={submissions.length > 0 ? "Submissions" : "Orders"}
                    value={submissions.length > 0
                        ? submissions.length
                        : (data.summary?.orders_credited || 0)}
                />
            </div>

            {/* Invite a Friend — "App Referral": coins bonus once the friend's
                first order is delivered. Only shown when the tenant has
                configured a nonzero bonus (Admin → Coins Config). */}
            {friendReferral.enabled && (
                <section className="mx-4 mt-4 rounded-2xl border border-border bg-card p-4" data-testid="friend-referral-card">
                    <div className="flex items-center gap-2">
                        <span className="shrink-0 w-9 h-9 rounded-xl bg-amber-500/15 text-amber-600 inline-flex items-center justify-center">
                            <Users size={18} />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">Invite a friend to the app</p>
                            <p className="text-[11px] text-muted-foreground">
                                Earn {friendRewardText} when your friend places their first order.
                            </p>
                        </div>
                    </div>
                    <div className={`mt-3 grid gap-2 ${friendReferral.bonus_wallet_amount > 0 && friendReferral.bonus_coins > 0 ? "grid-cols-3" : "grid-cols-2"}`}>
                        <div className="rounded-xl bg-muted/50 p-2.5 text-center">
                            <p className="text-base font-extrabold leading-none">{friendReferral.friends_referred}</p>
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Friends joined</p>
                        </div>
                        {friendReferral.bonus_wallet_amount > 0 && (
                            <div className="rounded-xl bg-muted/50 p-2.5 text-center">
                                <p className="text-base font-extrabold leading-none inline-flex items-center gap-1 justify-center">
                                    <Wallet size={13} className="text-emerald-600" /> {inr(friendReferral.wallet_earned)}
                                </p>
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Wallet earned</p>
                            </div>
                        )}
                        {friendReferral.bonus_coins > 0 && (
                            <div className="rounded-xl bg-muted/50 p-2.5 text-center">
                                <p className="text-base font-extrabold leading-none inline-flex items-center gap-1 justify-center">
                                    <Coins size={13} className="text-amber-600" /> {friendReferral.coins_earned}
                                </p>
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Coins earned</p>
                            </div>
                        )}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button data-testid="friend-referral-copy" onClick={copyFriend} className="flex-1 bg-muted hover:bg-muted/80 rounded-xl py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2">
                            {copiedFriend ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy invite</>}
                        </button>
                        <button data-testid="friend-referral-share" onClick={shareFriend} className="bg-primary text-primary-foreground rounded-xl px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2">
                            <Share2 size={14} /> Share
                        </button>
                    </div>
                </section>
            )}

            </div>
            <div className="min-w-0 lg:[&>*:first-child]:mt-4">
            {/* Add Shop — customer submits a new shop as a referral */}
            {cfg.customer_shop_submission_enabled !== false && (
                <section className="mx-4 mt-4" data-testid="add-shop-card">
                    <button
                        type="button"
                        onClick={() => setShowAddShop(true)}
                        data-testid="add-shop-open"
                        className="w-full flex items-center gap-3 rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-4 text-left active:scale-[0.99] transition"
                    >
                        <span className="shrink-0 w-11 h-11 rounded-xl bg-primary/15 text-primary inline-flex items-center justify-center">
                            <Plus size={22} />
                        </span>
                        <span className="flex-1 min-w-0">
                            <p className="font-bold text-sm">Know a shop? Add it here</p>
                            <p className="text-[11px] text-muted-foreground line-clamp-2">
                                Submit a shop you'd like to see on the app. If approved, you're the referrer.
                            </p>
                        </span>
                    </button>
                </section>
            )}

            {/* My submissions — customer sees status of every shop they added */}
            {submissions.length > 0 && (
                <section className="mx-4 mt-6" data-testid="my-submissions">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-2">
                        <Inbox size={14} /> My submitted shops
                    </h3>
                    <ul className="space-y-2">
                        {submissions.map((s) => (
                            <li
                                key={s.id}
                                className="bg-card border border-border rounded-2xl p-3"
                                data-testid={`my-submission-${s.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="shrink-0 w-11 h-11 rounded-xl bg-muted flex items-center justify-center">
                                        <Store size={18} className="text-muted-foreground" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-bold text-sm truncate">{s.shop_name}</p>
                                            <SubmissionStatusBadge status={s.status} />
                                        </div>
                                        <p className="text-[11px] text-muted-foreground truncate">
                                            {s.owner_name} · {s.city}
                                            {s.business_category_name ? ` · ${s.business_category_name}` : ""}
                                        </p>
                                        <p className="text-[11px] text-muted-foreground mt-0.5">
                                            {new Date(s.created_at).toLocaleDateString(undefined, {
                                                day: "numeric", month: "short", year: "numeric",
                                            })}
                                        </p>
                                        {s.status === "REJECTED" && s.admin_note && (
                                            <p className="mt-1 text-[11px] text-red-700 bg-red-50 rounded px-2 py-1">
                                                Not accepted: {s.admin_note}
                                            </p>
                                        )}
                                        {s.status === "APPROVED" && (
                                            <p className="mt-1 text-[11px] text-emerald-700">
                                                Approved — you're set as the referrer. Earnings will start once the shop goes live.
                                            </p>
                                        )}
                                        {s.status === "PENDING" && (
                                            <p className="mt-1 text-[11px] text-amber-700">
                                                Waiting for admin review — usually within 24 hours.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Shops linked */}
            <section className="mx-4 mt-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-2"><Store size={14} /> Your shops</h3>
                {data.shops.length === 0 ? (
                    <div className="bg-card border border-dashed border-border rounded-2xl p-6 text-center text-muted-foreground text-sm">
                        Share your code with shop owners. Once a shop is created using your code, it shows up here with live earnings.
                    </div>
                ) : (
                    <ul className="space-y-2.5">
                        {data.shops.map((s) => (
                            <li key={s.id} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3" data-testid={`referral-shop-${s.id}`}>
                                {s.logo_url ? <img src={s.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover" /> :
                                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center"><Store size={20} className="text-muted-foreground" /></div>}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm line-clamp-1">{s.name}</p>
                                    <p className="text-[11px] text-muted-foreground">{s.earnings_orders || 0} order{s.earnings_orders === 1 ? "" : "s"} credited</p>
                                </div>
                                <span className="text-base font-extrabold text-emerald-700">{inr(s.earnings_total || 0)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* Earnings ledger */}
            {data.transactions.length > 0 && (
                <section className="mx-4 mt-6">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2 inline-flex items-center gap-2"><Wallet size={14} /> Recent earnings</h3>
                    <ul className="bg-card border border-border rounded-2xl divide-y divide-border">
                        {data.transactions.map((t) => (
                            <li key={t.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`referral-tx-${t.id}`}>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold capitalize">{t.mode === "ONE_TIME" ? "One-time bonus" : "Order payout"}</p>
                                    <p className="text-[11px] text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
                                </div>
                                <span className="font-extrabold text-emerald-700">+{inr(t.amount)}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            </div>
            </div>
            {showAddShop && <AddShopModal onClose={() => setShowAddShop(false)} onSubmitted={loadSubmissions} />}
        </div>
    );
}

function SubmissionStatusBadge({ status }) {
    const map = {
        PENDING:  { icon: Clock,        cls: "bg-amber-100 text-amber-800",   label: "Pending"  },
        APPROVED: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
        REJECTED: { icon: XCircle,      cls: "bg-red-100 text-red-800",       label: "Rejected" },
    };
    const cfg = map[status] || map.PENDING;
    const Icon = cfg.icon;
    return (
        <span className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${cfg.cls}`}>
            <Icon size={11} /> {cfg.label}
        </span>
    );
}

function AddShopModal({ onClose, onSubmitted }) {
    const [form, setForm] = useState({
        shop_name: "", owner_name: "", owner_mobile: "",
        address_line: "", city: "", business_category_id: "", notes: "",
    });
    const [categories, setCategories] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Reuse the existing tenant business-categories endpoint so the
        // dropdown always mirrors what Admin → Business Categories has
        // configured (Food, Grocery, Electronics, Pharmacy, …).
        api.get("/business-categories").then(({ data }) => {
            setCategories(Array.isArray(data) ? data : []);
        }).catch(() => setCategories([]));
    }, []);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        // Client-side sanity so the user gets instant feedback.
        const required = ["shop_name", "owner_name", "owner_mobile", "address_line", "city"];
        for (const k of required) {
            if (!form[k].trim()) {
                toast.error("Please fill all required fields");
                return;
            }
        }
        const digits = form.owner_mobile.replace(/\D/g, "");
        if (digits.length < 8) {
            toast.error("Please enter a valid mobile number");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/customer/shop-submissions", form);
            toast.success(data?.message || "Shop referral submitted.");
            onSubmitted?.();
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit shop");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" data-testid="add-shop-modal" onClick={onClose}>
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="w-full sm:max-w-md bg-background rounded-t-3xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold">Add a shop</h2>
                    <button type="button" onClick={onClose} className="p-2 -mr-2 rounded-lg hover:bg-muted" data-testid="add-shop-close">
                        <X size={18} />
                    </button>
                </div>
                <p className="text-xs text-muted-foreground -mt-2 mb-4">
                    Submit shop details you know. Our team will contact the owner and add them to the app. You'll be credited as the referrer.
                </p>

                <Field label="Shop name*"       value={form.shop_name}    onChange={upd("shop_name")}    testid="add-shop-name"        placeholder="e.g. Radha General Store" />
                <Field label="Owner name*"      value={form.owner_name}   onChange={upd("owner_name")}   testid="add-shop-owner-name"  placeholder="Full name" />
                <Field label="Owner mobile*"    value={form.owner_mobile} onChange={upd("owner_mobile")} testid="add-shop-owner-mobile" inputMode="tel" placeholder="10-digit mobile" />
                <Field label="Address*"         value={form.address_line} onChange={upd("address_line")} testid="add-shop-address"     placeholder="Shop no, street, landmark" />
                <Field label="City*"            value={form.city}         onChange={upd("city")}         testid="add-shop-city"        placeholder="e.g. Mumbai" />
                {categories.length > 0 && (
                    <label className="block mt-3">
                        <span className="text-xs font-semibold text-muted-foreground">Business category</span>
                        <select
                            value={form.business_category_id}
                            onChange={upd("business_category_id")}
                            data-testid="add-shop-business-category"
                            className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                            <option value="">Not sure / select later</option>
                            {categories.map((c) => (
                                <option key={c.id} value={c.id}>{c.name || c.slug}</option>
                            ))}
                        </select>
                    </label>
                )}
                <label className="block mt-3">
                    <span className="text-xs font-semibold text-muted-foreground">Notes (optional)</span>
                    <textarea
                        value={form.notes}
                        onChange={upd("notes")}
                        rows={3}
                        maxLength={1000}
                        data-testid="add-shop-notes"
                        placeholder="Anything the team should know about this shop"
                        className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                </label>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="add-shop-submit"
                    className="mt-5 w-full rounded-2xl bg-primary text-primary-foreground font-bold py-3 disabled:opacity-50"
                >
                    {busy ? "Submitting…" : "Submit shop"}
                </button>
            </form>
        </div>
    );
}

function Field({ label, testid, ...rest }) {
    return (
        <label className="block mt-3">
            <span className="text-xs font-semibold text-muted-foreground">{label}</span>
            <input
                {...rest}
                data-testid={testid}
                className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
        </label>
    );
}

function Tile({ icon: Icon, label, value }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-3 text-center">
            <Icon size={16} className="text-muted-foreground mx-auto" />
            <p className="text-base font-extrabold mt-1 leading-none">{value}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
        </div>
    );
}
